import { useContext } from 'react'
import { AuthContext } from './auth/AuthProvider'
import type { AuthContextInterface } from './auth/types'

/**
 * Hook para acessar o contexto de autenticação com type safety.
 *
 * Este hook garante que:
 * - O contexto está disponível (componente está dentro do AuthProvider)
 * - Todas as funções e propriedades são tipadas corretamente
 * - Não é necessário verificar se as funções são undefined
 *
 * @throws {Error} Se usado fora do AuthProvider
 * @returns {AuthContextInterface} Contexto de autenticação
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { login, logout, isAuthenticated } = useAuth()
 *
 *   return (
 *     <div>
 *       {isAuthenticated() ? (
 *         <button onClick={() => logout()}>Sair</button>
 *       ) : (
 *         <button onClick={() => login('user', 'pass')}>Entrar</button>
 *       )}
 *     </div>
 *   )
 * }
 * ```
 */
export function useAuth(): AuthContextInterface {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error(
      'useAuth deve ser usado dentro de um AuthProvider. ' +
        'Certifique-se de que o componente está envolvido pelo <AuthProvider>.'
    )
  }

  return context
}
